import {
  getPointMultiplier,
  postPointMultiplierDraft,
  postPointMultiplierPublish,
  getPointMultiplierDetails,
  getPointMultiplierCsvFile,
  updatePointMultiplierDraft,
  validatePointMultiplierName
} from '@/services/pointMultiplier';
import {useQueries, useMutation, keepPreviousData} from '@tanstack/react-query';
import colConfig from '@/services/MockData/PointMultiplier/PointMultiplierColConfig.json';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  setInitialPointMultiplierName,
  setPointMultiplierFormData
} from '@/store/slices/pointMultiplier';
import {useNavigate, useParams} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  getDateFromISOString,
  getFormattedDateString,
  handleNameValidationViaCallbackFn,
  getUUID,
  buildQueryParams
} from '@/utils/commons';
import {STATUS_VALUES} from '@/utils/constants/commonConstants';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {useCallback, useMemo, useEffect, useRef, useState} from 'react';
import {GetFormattedPointMultiplierData} from '@/models/PointMultiplierModel';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import isArray from 'lodash-es/isArray';
import {validate} from 'uuid';
import {useErrorHandler} from '@/hooks/useErrorHandler';
import {logger} from '@/helpers/logger';

const initialSortConfig = {
  column: colConfig[0].name_of_db_column,
  direction: 'asc'
};

export const usePointMultiplier = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {handleApiError} = useErrorHandler();
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  const {id} = useParams();
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex, pageSize]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['POINT_MULTIPLIER', queryParams, searchArr],
        queryFn: () => getPointMultiplier(queryParams, searchArr),
        placeholderData: keepPreviousData
      }
    ]
  });

  const pointMultiplierDraftMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'draft'],
    mutationFn: (data) => postPointMultiplierDraft({data})
  });

  const pointMultiplierDraftUpdateMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'draft'],
    mutationFn: (data) => updatePointMultiplierDraft({data})
  });

  const pointMultiplierPublishMutation = useMutation({
    mutationKey: ['POINT_MULTIPLIER', 'publish'],
    mutationFn: (data) => postPointMultiplierPublish({data})
  });

  const handlePublish = async (formData) => {
    try {
      const cleanFormData = removeUndefinedFields(formData);
      const formattedFormData = formatFormData(cleanFormData);
      const response = await pointMultiplierPublishMutation.mutateAsync({
        ...formattedFormData,
        status: STATUS_VALUES.Published
      });
      if (response?.success) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'New point multiplier published',
            type: 'info'
          })
        );
        dispatch(setPointMultiplierFormData({}));
        navigate(ROUTES.POINT_MULTIPLIER);
        return {success: true};
      } else {
        const errMsg = 'Point multiplier failed to publish';
        dispatch(
          setToastNotifications({show: true, message: errMsg, type: 'error'})
        );
        return {success: false, error: errMsg};
      }
    } catch (error) {
      logger.error('Point multiplier publish error', error);
      const errMsg = 'Point multiplier failed to publish';
      dispatch(
        setToastNotifications({show: true, message: errMsg, type: 'error'})
      );
      return {success: false, error: errMsg};
    }
  };

  const removeUndefinedFields = (obj) => {
    const cleanObj = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== undefined) {
        if (
          typeof obj[key] === 'object' &&
          obj[key] !== null &&
          !isArray(obj[key])
        ) {
          const nestedClean = removeUndefinedFields(obj[key]);
          if (Object.keys(nestedClean).length > 0) {
            cleanObj[key] = nestedClean;
          }
        } else {
          cleanObj[key] = obj[key];
        }
      }
    });
    delete cleanObj['showError'];
    delete cleanObj['_persist'];
    return cleanObj;
  };

  const formatFormData = (formData) => {
    if ('activeEndDate' in formData) {
      formData['endDate'] = getFormattedDateString(formData.activeEndDate);
    }
    if ('activeStartDate' in formData) {
      formData['startDate'] = getFormattedDateString(formData.activeStartDate);
    }
    if ('modifierName' in formData) {
      formData['multiplierName'] = formData.modifierName;
    }
    delete formData['activeEndDate'];
    delete formData['activeStartDate'];
    delete formData['modifierName'];

    if ('endDate' in formData) {
      formData['hasEndDate'] = true;
    } else {
      formData['hasEndDate'] = false;
    }

    if (formData.rulesCriteria === 'Products') {
      delete formData.applicationId;
    }

    return {
      ...formData
    };
  };

  const handleDraft = async (formData) => {
    const cleanFormData = removeUndefinedFields(formData);
    const formattedFormData = formatFormData(cleanFormData);
    try {
      const requestData = {
        ...formattedFormData,
        status: STATUS_VALUES.Draft,
        uuid:
          formattedFormData.uuid || getUUID(formattedFormData.multiplierName)
      };
      let response;
      if (!formattedFormData.uuid) {
        //create a new draft if uuid is not present
        response = await pointMultiplierDraftMutation.mutateAsync(requestData);
      } else {
        response =
          await pointMultiplierDraftUpdateMutation.mutateAsync(requestData);
      }

      if (response?.success) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'New point multiplier saved',
            type: 'info'
          })
        );
        dispatch(setPointMultiplierFormData({}));
        navigate(ROUTES.POINT_MULTIPLIER);
        return {success: true};
      } else {
        const errMsg = 'Point multiplier failed to save';
        dispatch(
          setToastNotifications({show: true, message: errMsg, type: 'error'})
        );
        return {success: false, error: errMsg};
      }
    } catch (error) {
      logger.error('Point multiplier save error', error);
      const errMsg = 'Point multiplier failed to save';
      dispatch(
        setToastNotifications({show: true, message: errMsg, type: 'error'})
      );
      return {success: false, error: errMsg};
    }
  };

  const handleNameValidation = async (queryParams) => {
    const data = await handleNameValidationViaCallbackFn(
      validatePointMultiplierName,
      queryParams
    );
    return data;
  };

  const resultsData = useMemo(() => data || {}, [data]);
  const parsedData = useMemo(
    () => GetFormattedPointMultiplierData(resultsData),
    [resultsData]
  );

  // Rapid page/filter changes can break the keepPreviousData chain, leaving
  // `data` briefly undefined mid-fetch. Serve the last good payload during
  // that gap; settled results always pass through.
  const lastGoodDataRef = useRef(null);
  const pointMultiplierData = useMemo(() => {
    const hasRows = parsedData?.data?.length > 0;
    if (hasRows || !isFetching) {
      lastGoodDataRef.current = parsedData;
      return parsedData;
    }
    return lastGoodDataRef.current ?? parsedData;
  }, [parsedData, isFetching]);

  const handleGetPointMultiplierDetails = useCallback(async () => {
    const queryParams = validate(id) ? `uuid=${id}` : `id=${id}`;
    try {
      setLoading(true);
      const d = await getPointMultiplierDetails(queryParams);
      setLoading(false);
      return d;
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  }, [id, handleApiError]);

  const getPointMultiplierDetailsById = useCallback(async () => {
    try {
      let result = await handleGetPointMultiplierDetails();
      result = result.data?.[0] || [];
      const updatedResponse = {
        rulesCriteria: result?.rulesCriteria,
        activeStartDate: getDateFromISOString(result?.startDate),
        modifierName: result?.multiplierName,
        category: result?.category,
        rulesPriority: result?.rulesPriority,
        multiplierValue: result?.multiplierValue,
        segment: result?.segment,
        channel: result?.channel,
        activeEndDate: getDateFromISOString(result?.endDate),
        hasEndDate: result?.hasEndDate,
        metadata: result?.metadata,
        status: result?.status,
        uuid: result?.uuid,
        id: result?.id
      };
      dispatch(setInitialPointMultiplierName(result?.multiplierName));
      dispatch(setPointMultiplierFormData(updatedResponse));
    } catch (error) {
      handleApiError(error);
    }
  }, [handleGetPointMultiplierDetails, dispatch, handleApiError]);

  useEffect(() => {
    if (id) {
      getPointMultiplierDetailsById();
    }
  }, [getPointMultiplierDetailsById, id]);

  const onDownloadBtnClick = async (selectedIds, excludeIds = []) => {
    try {
      const body = {
        ids: selectedIds,
        excludeIds,
        filters: searchArr
      };
      const data = await getPointMultiplierCsvFile(queryParams, body);
      const fileName = `PointMultiplier_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      handleApiError(error);
    }
  };

  return {
    pointMultiplierData,
    pointMultiplierColConfigData: colConfig,
    handleDraft,
    handlePublish,
    handleNameValidation,
    isDraftLoading: pointMultiplierDraftMutation.isPending,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    setSearchArry,
    searchArr,
    isLoading,
    isFetching,
    onDownloadBtnClick,
    handleResetAll,
    loading,
    sortingObj
  };
};
